<script setup lang="ts">
/**
 * A placeholder shaped like what is coming.
 *
 * The console showed a grey paragraph reading "Načítám…". The till showed
 * NOTHING while a lazily-imported dialog's chunk resolved — eleven components
 * behind `defineAsyncComponent` with no `loadingComponent` and no `delay`, so
 * on a slow connection a server pressed "Zaplatit" and the screen did not
 * change, which is indistinguishable from the press not registering.
 *
 * It carries NO TEXT, deliberately. This package holds no copy — see the note
 * in `base.css` — and a skeleton with a caption is a spinner with extra steps.
 * What it is announced as belongs to the caller, which already has a
 * `role="status"` region and already knows what is loading.
 *
 * `aria-hidden` because the shape is decoration: the region around it says "one
 * moment", and a screen reader reading out nine blank rectangles is noise.
 */

withDefaults(
  defineProps<{
    /** How many lines. A list of six gets six, so the page does not jump. */
    lines?: number;
    /** CSS height of each line. */
    height?: string;
    /** Last line short, the way a paragraph ends. Off for table rows. */
    ragged?: boolean;
  }>(),
  { lines: 3, height: "1em", ragged: true },
);
</script>

<template>
  <div class="skeleton" aria-hidden="true">
    <span
      v-for="line in lines"
      :key="line"
      class="gf-skeleton skeleton__line"
      :style="{
        height,
        width: ragged && line === lines && lines > 1 ? '60%' : '100%',
      }"
    />
  </div>
</template>

<style scoped>
.skeleton {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-2);
  width: 100%;
}

.skeleton__line {
  display: block;
}
</style>
